import React, { useEffect, useState } from 'react';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, FlexBox, Typography, AbsoluteBox, InputField, Grid } from '@kis-technologies/kis-ui';
import { Keyboard } from 'components/Keyboard';
import { useKeyboard } from 'hooks/useKeyboard';
import { selectors as configSelectors, Configuration, Messages } from 'services/configuration';
import { selectors as customerSelectors } from 'services/customer';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { validateEmail } from 'utils/validation';
import { useStyles } from '../styled';
import { ComponentProps } from '../types';

export const DeliveryEmailCollection = (props: ComponentProps) => {
  const { onNext, onPrev } = props;
  const { t, i18n } = useTranslation();

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const savedEmail = useSelector(customerSelectors.selectSavedEmail) as string;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');
  const emailSuggestions: string[] = get(config, `messaging.${i18n.language}.emailSuggestions.buttons`, [
    '@gmail.com',
    '@yahoo.com',
    '@outlook.com'
  ]);
  const messages: Messages = get(config, `${ticketingPath}.ticketDeliverySettings.email.dataCollectionMessages`, {});
  const headerText: string = get(messages, `header.${i18n.language}`, t('delivery.whereShouldWeSend'));
  const subheaderText: string = get(messages, `subheader.${i18n.language}`, t('delivery.enterYourEmail'));
  const placeholderText: string = get(messages, `placeholder.${i18n.language}`, t('commonText.email'));
  const backButtonText: string = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const nextButtonText: string = get(messages, `buttons.${i18n.language}[1]`, t('commonText.continue'));

  const [value, setValue] = useKeyboard(savedEmail || '');
  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);

  const classes = useStyles({ fontFamily })();

  useEffect(() => {
    setIsValidEmail(Boolean(value && validateEmail().safeParse(value).success));
  }, [value]);

  const handleKeyboardClick = (_: React.MouseEvent<HTMLButtonElement>, v: string) => {
    setValue(v);
  };

  return (
    <>
      <Grid className={classes.outerContainer}>
        <Grid className={classes.deliveryInput}>
          <Box flexOverride="none" margin="6">
            <Typography fontSize={72} fontWeight={800} align="center">
              {headerText}
            </Typography>
          </Box>
          <Typography className={classes.deliverySubHeading}>{subheaderText}</Typography>
          <Grid className={classes.deliveryInputBox}>
            <InputField
              autoFocus
              size="large"
              align="center"
              name="value"
              placeholder={placeholderText}
              fullWidth
              value={value}
            />
          </Grid>
        </Grid>
      </Grid>
      <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
        <FlexBox fullHeight align="center" direction="column">
          <Keyboard
            language={i18n.language}
            onClick={handleKeyboardClick}
            onNext={() => onNext('email', value)}
            onPrev={onPrev}
            isNextDisabled={!isValidEmail}
            nextText={nextButtonText}
            prevText={backButtonText}
            suggestions={emailSuggestions}
            onSuggestionClick={handleKeyboardClick}
          />
        </FlexBox>
      </AbsoluteBox>
    </>
  );
};
